import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/apiClient';
import quickbooksLogo from '../assets/logos/integrations/quickbooks.svg';
import xeroLogo from '../assets/logos/integrations/xero.svg';

const PROVIDER_META = {
  quickbooks: { name: 'QuickBooks', logo: quickbooksLogo, available: true },
  xero:       { name: 'Xero',       logo: xeroLogo,       available: false },
};

const ENTITY_PROVIDERS = {
  bills:            ['quickbooks', 'xero'],
  payments:         ['quickbooks', 'xero'],
  banking:          ['quickbooks'],
  purchase_orders:  ['quickbooks', 'xero'],
  invoices_sent:    ['quickbooks', 'xero'],
  vendors:          ['quickbooks', 'xero'],
};

const SYNCABLE = new Set(['quickbooks']);

/**
 * SyncBanner — contextual sync prompts on data pages.
 *
 * Props:
 *   entity       — 'bills' | 'payments' | 'banking' | 'purchase_orders' | 'vendors'
 *   label        — human-readable: 'bills', 'purchase orders', 'vendors', etc.
 *   onSynced     — callback() after sync completes (to refetch data)
 */
const SyncBanner = ({ entity, label, onSynced }) => {
  const [integrations, setIntegrations] = useState([]);
  const [preference, setPreference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [syncDone, setSyncDone] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connecting, setConnecting] = useState(null);
  // Multi-step follow-up
  const [step, setStep] = useState('initial'); // 'initial' | 'follow-up' | 'done'
  const [pullEnabled, setPullEnabled] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      apiFetch('/api/integrations').then(r => r.json()).catch(() => ({ integrations: [] })),
      apiFetch('/api/sync/preferences').then(r => r.json()).catch(() => ({ preferences: [] })),
    ]).then(([intData, prefData]) => {
      if (cancelled) return;

      const syncable = (intData.integrations || []).filter(i => i.status === 'active' && SYNCABLE.has(i.provider));
      setIntegrations(syncable);

      const prefs = (prefData.preferences || []).filter(p => syncable.some(i => i.provider === p.provider));
      const pullPref = prefs.find(p => p.entity === entity);
      const pushPref = prefs.find(p => p.entity === `${entity}_push`);
      // If either pull or push has been configured, treat as "preference set"
      const hasAnyPref = (pullPref && pullPref.enabled !== undefined) || (pushPref && pushPref.enabled !== undefined);
      setPreference(hasAnyPref ? (pullPref || pushPref) : false);

      if (pullPref && pullPref.enabled !== undefined) {
        setPullEnabled(pullPref.enabled);
      }
      if (pushPref && pushPref.enabled !== undefined) {
        setPushEnabled(pushPref.enabled);
      }
    }).finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [entity]);

  const handleSync = async (provider) => {
    setSyncing(provider);
    try {
      const entityRoute = entity === 'purchase_orders' ? 'bills' : entity;

      // Pull from provider (if pull enabled)
      if (pullEnabled !== false) {
        await apiFetch(`/api/sync/${provider}/${entityRoute}`, { method: 'POST' });
      }

      // Push to provider (only if push preference enabled)
      if (pushEnabled) {
        await apiFetch(`/api/sync/${provider}/${entityRoute}/push`, { method: 'POST' }).catch(() => {});
      }

      setSyncDone(true);
      onSynced?.();
      setTimeout(() => { setSyncDone(false); setSyncing(null); }, 2000);
    } catch {
      setSyncing(null);
    }
  };

  const handleSavePreferences = async (provider) => {
    setSyncing(provider);
    try {
      // Save pull preference
      await apiFetch('/api/sync/preferences', {
        method: 'PUT',
        body: JSON.stringify({ provider, entity, enabled: !!pullEnabled }),
      });
      // Save push preference
      await apiFetch('/api/sync/preferences', {
        method: 'PUT',
        body: JSON.stringify({ provider, entity: `${entity}_push`, enabled: !!pushEnabled }),
      });

      // If pull enabled, do initial sync
      if (pullEnabled) {
        const entityRoute = entity === 'purchase_orders' ? 'bills' : entity;
        await apiFetch(`/api/sync/${provider}/${entityRoute}`, { method: 'POST' });
        onSynced?.();
      }

      setSyncDone(true);
      setTimeout(() => setDismissed(true), 2000);
    } catch {
      setSyncing(null);
    }
  };

  const handleConnect = async (providerId) => {
    if (providerId !== 'quickbooks') return;
    setConnecting(providerId);
    try {
      const response = await apiFetch('/api/quickbooks/authorize');
      const data = await response.json();
      if (data.success && data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch { /* silent */ }
    setConnecting(null);
  };

  if (loading || dismissed) return null;

  // Already configured — show last synced indicator
  if (preference && (preference.enabled || pushEnabled)) {
    const provider = integrations.find(i => SYNCABLE.has(i.provider))?.provider;
    const meta = provider ? PROVIDER_META[provider] : null;
    const lastSynced = preference.last_synced_at;

    const formatSyncTime = (iso) => {
      if (!iso) return 'Never';
      const d = new Date(iso);
      const now = new Date();
      const diffMs = now - d;
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'Just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHrs = Math.floor(diffMin / 60);
      if (diffHrs < 24) return `${diffHrs}h ago`;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    };

    return (
      <div className="flex items-center gap-2 mb-4">
        {meta?.logo && <img src={meta.logo} alt="" className="w-4 h-4 shrink-0 opacity-40" />}
        {syncing ? (
          <>
            <div className="w-3 h-3 border border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
            <span className="text-[11px] text-zinc-400">Syncing...</span>
          </>
        ) : syncDone ? (
          <span className="text-[11px] text-emerald-500">Synced</span>
        ) : (
          <button
            onClick={() => provider && handleSync(provider)}
            className="text-[11px] text-zinc-400 hover:text-zinc-600 transition-colors"
          >
            Last synced {formatSyncTime(lastSynced)} · <span className="underline underline-offset-2">Sync now</span>
          </button>
        )}
      </div>
    );
  }

  const relevantProviders = (ENTITY_PROVIDERS[entity] || []).map(id => ({
    id,
    ...PROVIDER_META[id],
    connected: integrations.some(i => i.provider === id),
  }));

  // Connected integrations — show sync flow
  if (integrations.length > 0) {
    const provider = integrations[0];
    const meta = PROVIDER_META[provider.provider];

    // Follow-up step — ask about pull/push
    if (step === 'follow-up') {
      return (
        <div className="bg-white border border-zinc-200 rounded-lg mb-4 overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
            {meta?.logo && <img src={meta.logo} alt="" className="w-5 h-5 shrink-0" />}
            <span className="text-[13px] font-medium text-zinc-900">Sync {label} with {meta?.name}</span>
          </div>
          <div className="p-4 space-y-3">
            {/* Pull toggle */}
            <label className="flex items-center gap-3 cursor-pointer group">
              <button
                onClick={() => setPullEnabled(!pullEnabled)}
                className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200 ${pullEnabled ? 'bg-zinc-900' : 'bg-zinc-200'}`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200 mt-0.5 ${pullEnabled ? 'translate-x-4 ml-0.5' : 'translate-x-0 ml-0.5'}`} />
              </button>
              <div>
                <p className="text-sm text-zinc-800 group-hover:text-zinc-900">Import {label} from {meta?.name}</p>
                <p className="text-[11px] text-zinc-400">Pull {label} into Arbcor and keep them in sync</p>
              </div>
            </label>

            {/* Push toggle */}
            <label className="flex items-center gap-3 cursor-pointer group">
              <button
                onClick={() => setPushEnabled(!pushEnabled)}
                className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200 ${pushEnabled ? 'bg-zinc-900' : 'bg-zinc-200'}`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200 mt-0.5 ${pushEnabled ? 'translate-x-4 ml-0.5' : 'translate-x-0 ml-0.5'}`} />
              </button>
              <div>
                <p className="text-sm text-zinc-800 group-hover:text-zinc-900">Send new {label} to {meta?.name}</p>
                <p className="text-[11px] text-zinc-400">{label.charAt(0).toUpperCase() + label.slice(1)} created in Arbcor will be pushed to {meta?.name}</p>
              </div>
            </label>
          </div>
          <div className="px-4 py-3 border-t border-zinc-100 flex items-center justify-end gap-2">
            <button onClick={() => setDismissed(true)} className="text-[12px] text-zinc-400 hover:text-zinc-600 transition-colors">Skip</button>
            <button
              onClick={() => handleSavePreferences(provider.provider)}
              disabled={!!syncing}
              className="px-4 py-1.5 bg-zinc-900 text-white text-[12px] font-medium rounded-md hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              {syncing ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      );
    }

    // Initial prompt
    return (
      <div className="flex items-center gap-3 px-4 py-3 bg-white border border-zinc-200 rounded-lg mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {meta?.logo && <img src={meta.logo} alt="" className="w-5 h-5 shrink-0" />}
          <p className="text-[13px] text-zinc-600">
            {syncDone ? (
              <span className="text-emerald-600 font-medium">Synced — {label} will stay up to date</span>
            ) : syncing ? (
              <span className="text-zinc-500">Syncing {label}...</span>
            ) : (
              <>Sync {label} with <span className="font-medium text-zinc-900">{meta?.name}</span>?</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!syncing && !syncDone && (
            <>
              <button onClick={() => setStep('follow-up')} className="text-[12px] text-zinc-400 hover:text-zinc-600 transition-colors">No</button>
              <button onClick={() => setStep('follow-up')}
                className="px-3 py-1.5 bg-zinc-900 text-white text-[12px] font-medium rounded-md hover:bg-zinc-800 transition-colors">
                Yes
              </button>
            </>
          )}
          {syncing && <div className="w-4 h-4 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />}
          {syncDone && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-emerald-500">
              <path d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
      </div>
    );
  }

  // No integrations connected
  return (
    <>
      <div className="flex items-center gap-3 px-4 py-3 bg-zinc-50 border border-zinc-100 rounded-lg mb-4">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-400 shrink-0">
          <path d="M15 7h3a5 5 0 0 1 5 5 5 5 0 0 1-5 5h-3m-6 0H6a5 5 0 0 1-5-5 5 5 0 0 1 5-5h3" /><path d="M8 12h8" />
        </svg>
        <p className="text-[13px] text-zinc-500 flex-1">
          Connect your accounting software to sync {label} automatically
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setDismissed(true)} className="text-[12px] text-zinc-400 hover:text-zinc-600 transition-colors">Dismiss</button>
          <button onClick={() => setShowConnectModal(true)}
            className="px-3 py-1.5 bg-zinc-900 text-white text-[12px] font-medium rounded-md hover:bg-zinc-800 transition-colors">
            Connect
          </button>
        </div>
      </div>

      {showConnectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={() => setShowConnectModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
              <div>
                <h3 className="text-[15px] font-semibold text-zinc-900">Connect to sync {label}</h3>
                <p className="text-[12px] text-zinc-400 mt-0.5">Choose your accounting platform</p>
              </div>
              <button onClick={() => setShowConnectModal(false)} className="w-7 h-7 rounded-md hover:bg-zinc-100 flex items-center justify-center text-zinc-400 hover:text-zinc-600 transition-colors">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M12 4L4 12M4 4l8 8" /></svg>
              </button>
            </div>
            <div className="p-3 space-y-1.5">
              {relevantProviders.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-3 py-3 rounded-md border border-zinc-100 hover:border-zinc-200 transition-colors">
                  <img src={p.logo} alt={p.name} className="w-8 h-8 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-zinc-900">{p.name}</p>
                    {p.connected ? (
                      <p className="text-[11px] text-emerald-600">Connected</p>
                    ) : !p.available ? (
                      <p className="text-[11px] text-zinc-300">Coming soon</p>
                    ) : null}
                  </div>
                  {p.connected ? (
                    <button onClick={() => { setShowConnectModal(false); setStep('follow-up'); }}
                      className="px-3 py-1.5 bg-zinc-900 text-white text-[11px] font-medium rounded-md hover:bg-zinc-800 transition-colors">
                      Set up sync
                    </button>
                  ) : p.available ? (
                    <button onClick={() => handleConnect(p.id)} disabled={connecting === p.id}
                      className="px-3 py-1.5 border border-zinc-200 text-zinc-700 text-[11px] font-medium rounded-md hover:bg-zinc-50 transition-colors disabled:opacity-50">
                      {connecting === p.id ? 'Connecting...' : 'Connect'}
                    </button>
                  ) : (
                    <span className="text-[11px] text-zinc-300 px-3 py-1.5">Soon</span>
                  )}
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-zinc-100">
              <button onClick={() => { setShowConnectModal(false); navigate('/dashboard/integrations'); }}
                className="text-[12px] text-zinc-400 hover:text-zinc-700 transition-colors">
                See all integrations &rarr;
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SyncBanner;
